import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/client';
import { Loader2, MapPin, Building2, AlertTriangle, Activity, Info } from 'lucide-react';

// Simple geographic layout of Spain's 19 CCAA in a responsive grid.
// Each cell has row/col to approximate real geographic placement.
const LAYOUT = [
  { key: 'Galicia',              row: 1, col: 1, short: 'GAL' },
  { key: 'Asturias',             row: 1, col: 2, short: 'AST' },
  { key: 'Cantabria',            row: 1, col: 3, short: 'CTB' },
  { key: 'País Vasco',           row: 1, col: 4, short: 'PV'  },
  { key: 'Navarra',              row: 1, col: 5, short: 'NAV' },
  { key: 'La Rioja',             row: 2, col: 4, short: 'RIO' },
  { key: 'Aragón',               row: 2, col: 5, short: 'ARA' },
  { key: 'Cataluña',             row: 2, col: 6, short: 'CAT' },
  { key: 'Castilla y León',      row: 2, col: 2, short: 'CyL' },
  { key: 'Madrid',               row: 2, col: 3, short: 'MAD' },
  { key: 'Extremadura',          row: 3, col: 2, short: 'EXT' },
  { key: 'Castilla-La Mancha',   row: 3, col: 3, short: 'CLM' },
  { key: 'Comunidad Valenciana', row: 3, col: 5, short: 'CVA' },
  { key: 'Baleares',             row: 3, col: 6, short: 'BAL' },
  { key: 'Andalucía',            row: 4, col: 3, short: 'AND' },
  { key: 'Murcia',               row: 4, col: 4, short: 'MUR' },
  { key: 'Ceuta',                row: 5, col: 2, short: 'CEU' },
  { key: 'Melilla',              row: 5, col: 3, short: 'MEL' },
  { key: 'Canarias',             row: 5, col: 5, short: 'CAN' },
];

const METRICS = [
  { key: 'coverage', label: 'Cobertura (nodos+torres)', icon: Activity,       field: 'coverage_total' },
  { key: 'sites',    label: 'Sites por CCAA',           icon: Building2,      field: 'sites' },
  { key: 'incidents',label: 'Incidencias activas',      icon: AlertTriangle,  field: 'incidents_active' },
];

// Green (low) → high (bright #A3FF12). We use lightness in HSL for gradient.
const cellStyle = (pct, hue = 78) => {
  const alpha = 0.15 + (pct / 100) * 0.85;
  const border = pct > 60 ? '#A3FF12' : pct > 20 ? 'rgba(163,255,18,0.4)' : 'rgba(255,255,255,0.1)';
  return {
    background: `hsla(${hue}, 100%, 55%, ${alpha})`,
    borderColor: border,
    boxShadow: pct > 75 ? '0 0 24px rgba(163,255,18,0.35)' : 'none',
  };
};

const CoverageHeatmap = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('coverage');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/coverage/by-ccaa');
        if (!cancel) setItems(data);
      } catch { /* silent */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const byKey = useMemo(() => Object.fromEntries(items.map((i) => [i.ccaa, i])), [items]);
  const activeMetric = METRICS.find((m) => m.key === metric);
  const max = Math.max(1, ...items.map((i) => i[activeMetric.field] || 0));

  const getPct = (it) => {
    if (!it) return 0;
    return Math.round(((it[activeMetric.field] || 0) / max) * 100);
  };

  // Hue per metric: green for coverage, blue for sites, red for incidents
  const hue = metric === 'incidents' ? 0 : metric === 'sites' ? 210 : 78;

  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-3 sm:p-5 lg:p-6" data-testid="coverage-heatmap">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-[#A3FF12]"/>
            <h2 className="text-lg lg:text-xl font-black text-white">Heatmap de cobertura</h2>
          </div>
          <p className="text-white/50 text-xs mt-1">Presencia de infraestructura por Comunidad Autónoma. Cambia la métrica para ver el ranking en calor.</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {METRICS.map((m) => {
            const Icon = m.icon;
            const active = metric === m.key;
            return (
              <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`heatmap-metric-${m.key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${
                  active ? 'bg-[#A3FF12] text-black border-[#A3FF12]' : 'bg-white/5 text-white/60 border-white/10 hover:text-white'
                }`}>
                <Icon size={12}/> {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#A3FF12]" size={28}/></div>
      ) : (
        <>
          <div className="grid gap-1.5 sm:gap-2" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gridTemplateRows: 'repeat(5, minmax(0, 1fr))' }}>
            {LAYOUT.map((cell) => {
              const it = byKey[cell.key];
              const pct = getPct(it);
              const val = it?.[activeMetric.field] ?? 0;
              return (
                <div key={cell.key} data-testid={`heatmap-cell-${cell.key}`}
                  title={`${cell.key} · ${val.toLocaleString('es-ES')}`}
                  style={{ gridColumn: cell.col, gridRow: cell.row, ...cellStyle(pct, hue) }}
                  className="rounded-lg border p-1.5 sm:p-2 lg:p-3 min-h-[60px] sm:min-h-[68px] flex flex-col justify-between transition-all hover:scale-[1.03] cursor-default overflow-hidden">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[9px] sm:text-[10px] font-black tracking-wider text-white/70 truncate">{cell.short}</span>
                    <span className="text-[9px] sm:text-[10px] font-bold text-white/50 flex-shrink-0">{pct}%</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-black text-[11px] sm:text-sm lg:text-lg leading-tight truncate tabular-nums">{val.toLocaleString('es-ES')}</div>
                    <div className="text-white/50 text-[8px] sm:text-[10px] leading-tight truncate hidden sm:block">{cell.key}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend + top 3 */}
          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-[11px] text-white/50">
              <Info size={12}/> Ranking por {activeMetric.label.toLowerCase()}:
              {items.slice(0, 3).map((it, i) => (
                <span key={it.ccaa} className="ml-1">
                  <b className="text-white">#{i + 1} {it.ccaa}</b>
                  <span className="text-white/40"> ({(it[activeMetric.field] || 0).toLocaleString('es-ES')})</span>
                  {i < 2 && <span className="text-white/20">·</span>}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-white/40">Menos</span>
              <div className="w-32 h-2 rounded-full" style={{ background: `linear-gradient(to right, hsla(${hue},100%,55%,0.15), hsla(${hue},100%,55%,1))` }}/>
              <span className="text-white/40">Más</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CoverageHeatmap;
